import {
  ClientAuthenticate,
  ClientAuthenticateResult,
  CoreRequestBody,
  CoreResponseBody,
  isClientAuthenticateResult,
  isPhoneAccountCreateResult,
  isUpdateVersionCheckResult,
  isDirectoryResolveResult,
  MessageRequestBody,
  MessageResponseBody,
  PhoneAccountCreate,
  PhoneAccountCreateResult,
  UpdateVersionCheck,
  UpdateVersionCheckResult,
  UserLoginCheck,
  DirectoryResolve,
  DirectoryResolveResult,
} from "./types";
import { Builder, parseStringPromise, processors } from "xml2js";

const CORE_REQUEST_URL =
  process.env.CORE_URL ||
  "https://4j8w5ydgo3.execute-api.us-west-2.amazonaws.com/core-request";
const MESSAGE_REQUEST_URL =
  process.env.MESSAGE_URL ||
  "https://4j8w5ydgo3.execute-api.us-west-2.amazonaws.com/message-request";

export * from "./types";

export class Core {
  authToken?: string;
  coreRequestCount = 1;
  messageRequestCount = 1;
  stateNotifyCount = 1;

  private static instance: Core;
  public static coreRequestUrl?: string;
  public static messageRequestUrl?: string;

  public static updateConfig(config: {
    coreRequestUrl?: string;
    messageRequestUrl?: string;
  }) {
    if (config.coreRequestUrl) {
      Core.coreRequestUrl = config.coreRequestUrl;
    }
    if (config.messageRequestUrl) {
      Core.messageRequestUrl = config.messageRequestUrl;
    }
  }

  public static getInstance(): Core {
    if (!Core.instance) {
      Core.instance = new Core();
    }
    return Core.instance;
  }

  async coreRequestWithAuthToken(
    body: CoreRequestBody,
    authToken: string
  ): Promise<CoreResponseBody> {
    this.authToken = authToken;
    return await this.coreRequest(body);
  }

  async messageRequestWithAuthToken(
    body: MessageRequestBody,
    authToken: string
  ): Promise<MessageResponseBody> {
    this.authToken = authToken;
    return await this.messageRequest(body);
  }

  async coreRequest(body: CoreRequestBody): Promise<CoreResponseBody> {
    let requestBody: object = {};
    if (Array.isArray(body)) {
      body.forEach((r) => Object.assign(requestBody, r));
    } else {
      requestBody = body;
    }
    var xmlObject = {
      CoreRequest: {
        $: {
          Id: this.coreRequestCount,
          ClientToken: this.authToken ?? "",
        },
        ...requestBody,
      },
    };
    var builder = new Builder({
      xmldec: {
        version: "1.0",
        encoding: "us-ascii",
        standalone: false,
      },
    });
    var xmlBody = builder.buildObject(xmlObject);
    console.log(xmlBody.toString());

    try {
      const response = await fetch(Core.coreRequestUrl || CORE_REQUEST_URL, {
        method: "POST",
        headers: {
          "content-type": "application/xml",
        },
        body: xmlBody,
      });
      this.coreRequestCount++;
      const text = await response.text();
      const xml = await parseStringPromise(text, {
        valueProcessors: [processors.parseNumbers, processors.parseBooleans],
        attrValueProcessors: [
          processors.parseNumbers,
          processors.parseBooleans,
        ],
        explicitArray: false,
      });
      return Promise.resolve(xml.CoreResponse);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async messageRequest(body: MessageRequestBody): Promise<MessageResponseBody> {
    let requestBody: object = {};
    if (Array.isArray(body)) {
      body.forEach((r) => Object.assign(requestBody, r));
    } else {
      requestBody = body;
    }
    var xmlObject = {
      MessageRequest: {
        $: {
          Id: this.messageRequestCount,
          ClientToken: this.authToken ?? "",
        },
        ...body,
      },
    };
    var builder = new Builder({
      xmldec: {
        version: "1.0",
        encoding: "us-ascii",
        standalone: false,
      },
    });
    var xmlBody = builder.buildObject(xmlObject);
    try {
      const response = await fetch(
        Core.messageRequestUrl || MESSAGE_REQUEST_URL,
        {
          method: "POST",
          headers: {
            "content-type": "application/xml",
          },
          body: xmlBody,
        }
      );
      this.messageRequestCount++;
      const text = await response.text();
      const xml = await parseStringPromise(text, {
        valueProcessors: [processors.parseNumbers, processors.parseBooleans],
        attrValueProcessors: [
          processors.parseNumbers,
          processors.parseBooleans,
        ],
        explicitArray: false,
      });
      return Promise.resolve(xml.MessageResponse);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async updateVersionCheck(
    body: UpdateVersionCheck
  ): Promise<UpdateVersionCheckResult> {
    const macCheckResponse = await this.coreRequest(body);
    return isUpdateVersionCheckResult(macCheckResponse)
      ? macCheckResponse
      : Promise.reject(new Error("UpdateVersionCheck response error"));
  }

  async phoneAccountCreate(
    body: PhoneAccountCreate
  ): Promise<PhoneAccountCreateResult> {
    const phoneAccountCreateResponse = await this.coreRequest(body);
    return isPhoneAccountCreateResult(phoneAccountCreateResponse)
      ? phoneAccountCreateResponse
      : Promise.reject(new Error("PhoneAccountCreateResult response error"));
  }

  async clientAuthenticate(
    body: (ClientAuthenticate | UserLoginCheck)[]
  ): Promise<ClientAuthenticateResult> {
    const clientAuthenticateResult = await this.coreRequest(body);
    return isClientAuthenticateResult(clientAuthenticateResult)
      ? clientAuthenticateResult
      : Promise.reject("ClientAuthenticateResult response error");
  }

  async directoryResolve(
    body: DirectoryResolve
  ): Promise<DirectoryResolveResult> {
    const directoryResolveResult = await this.coreRequest(body);
    return isDirectoryResolveResult(directoryResolveResult)
      ? directoryResolveResult
      : Promise.reject("DirectoryResolveResult response error");
  }
}
